/**
 * Octopus - EquipmentAlertsPage (Fase 1000, Plan 05)
 *
 * Central única de alertas por EQUIPAMENTO — rota `/equipments/:equipmentId/alerts`.
 * Concentra Combustível, Bateria, Telemetria/RPM, Clima (regras `db.alert_rules`)
 * e Conectividade (offline + cegueira de stream, editor real v1 sobre
 * `db.equipment_alert_config`). Motor/Painel e Geofence chegam no Plan 06.
 *
 * O botão "Alertas" da linha do equipamento em EquipmentsPage.js navega para
 * cá (o modal legado `EquipmentAlertConfig` deixa de abrir por esse botão).
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import {
  ArrowLeft,
  Search,
  Fuel,
  BatteryWarning,
  Gauge,
  CloudSun,
  Unplug,
  Loader2,
  AlertTriangle,
  Cog,
  ArrowLeftRight,
  Hexagon,
  Plus,
  Trash2,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Card, CardContent } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { cn } from '@/lib/utils';
import { severityConfig } from '@/lib/alertSeverity';
import { alertsApi } from '@/services/alertsApi';
import { getAlertConfig, putAlertConfig } from '@/services/alertConfigApi';
import { geofenceApi } from '@/services/geofenceApi';
import { useAuth } from '@/contexts/AuthContext';
import AlertCategorySection from '@/components/alerts/AlertCategorySection';
import AlertRuleRow from '@/components/alerts/AlertRuleRow';
import AlertRuleDialog, { DeleteRuleDialog } from '@/components/alerts/AlertRuleDialog';
import AlertRecipientsEditor from '@/components/alerts/AlertRecipientsEditor';
import GeofenceRuleEditor from '@/components/GeofenceRuleEditor';
import { validPhoneList } from '@/components/PhoneNumberInput';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BATTERY_HINT_RE = /bater|batt|soc|voltage/i;

// Severidade curada — MESMOS values/labels de MotorsConfig.js/severityConfig
// (D-01, zero drift entre a aba Motores e esta página).
const SEVERITY_OPTIONS = [
  { value: 'critical', label: 'Crítica' },
  { value: 'high', label: 'Alta' },
  { value: 'medium', label: 'Média' },
  { value: 'low', label: 'Baixa' },
];

const DEFAULT_GEOFENCE_TRIGGER = {
  trigger: 'on_exit',
  severity: 'medium',
  repeat_interval_minutes: 5,
  notify_email: false,
  notify_whatsapp: false,
  notification_emails: [],
  notification_phones: [],
};

const m3ToL = (m3) => (m3 == null ? '' : String(Math.round(m3 * 1000)));
const lToM3 = (l) => {
  const n = parseFloat(l);
  return Number.isFinite(n) && n > 0 ? n / 1000 : null;
};

/** Resumo de destinatários de UMA regra (db.alert_rules) — regras genéricas
 * (Combustível/Bateria/Telemetria-RPM/Clima/Offline) não herdam de nenhuma
 * categoria (isso só existe para Motor/Painel via engine_notification no
 * Plan 06) — cada regra é dona dos seus próprios destinatários. */
const ruleRecipientsSummary = (rule) => {
  const phones = rule?.notification_phones || [];
  const emails = rule?.notification_emails || [];
  const parts = [];
  if (rule?.notify_whatsapp) parts.push(phones.length ? `${phones.length} tel.` : 'sem tel.');
  if (rule?.notify_email) parts.push(emails.length ? `${emails.length} e-mail(s)` : 'sem e-mail');
  return parts.length ? parts.join(' · ') : 'sem notificação configurada';
};

const ruleIsWhatsappOrphan = (rule) => !!rule?.notify_whatsapp && !(rule?.notification_phones || []).length;

const matchesQuery = (text, query) => !query || String(text || '').toLowerCase().includes(query.toLowerCase());

/**
 * @param {object} props
 * @param {string} props.category - chave da categoria (fuel/battery/telemetry/weather/connectivity)
 * @param {string} props.label
 * @param {React.ComponentType} props.icon
 * @param {Array} props.rules
 * @param {string} props.query
 * @param {string} props.highlightId
 * @param {(rule: object|null) => void} props.onNewRule
 * @param {(rule: object) => void} props.onEditRule
 * @param {(rule: object) => void} props.onToggleRule
 * @param {(rule: object) => void} props.onDeleteRule
 */
const RuleCategorySection = ({
  category,
  label,
  icon,
  rules,
  query,
  highlightId,
  onNewRule,
  onEditRule,
  onToggleRule,
  onDeleteRule,
}) => {
  const filtered = rules.filter(
    (r) => matchesQuery(label, query) || matchesQuery(r?.name, query),
  );
  const hasOrphan = rules.some(ruleIsWhatsappOrphan);

  if (query && filtered.length === 0 && !matchesQuery(label, query)) {
    return null;
  }

  return (
    <AlertCategorySection icon={icon} label={label} ruleCount={rules.length} hasOrphan={hasOrphan}>
      {filtered.map((rule) => (
        <div
          key={rule.id}
          className={cn(rule.id === highlightId && 'ring-2 ring-primary/50 rounded-md transition-all')}
          data-testid={`alert-rule-row-${rule.id}`}
        >
          <AlertRuleRow
            rule={rule}
            recipientsSummary={ruleRecipientsSummary(rule)}
            onToggleEnabled={onToggleRule}
            onEdit={onEditRule}
            onDelete={onDeleteRule}
          />
        </div>
      ))}
      <div className="pt-2">
        <Button size="sm" variant="outline" className="h-8 text-sm" onClick={() => onNewRule(category)}>
          Nova regra
        </Button>
      </div>
    </AlertCategorySection>
  );
};

/**
 * Seção Geofence (Fase 1000, Plan 06): embute o `GeofenceRuleEditor` (por
 * gatilho, `compact`) do equipamento — sem modificar o componente. A lógica
 * de CRUD (vincular geofence, alternar/editar/excluir regra) é a MESMA de
 * `EquipmentGeofenceConfig.jsx`, portada aqui sem o wrapper `Dialog` (a
 * página já é a superfície; o modal legado segue intocado/aposentado
 * separadamente). Com o broadcast morto (Plan 02), `notification_phones`
 * por regra vira o escopo real de destinatários desta categoria.
 */
const GeofenceCategorySection = ({ equipmentId, query, highlightId }) => {
  const { user } = useAuth();
  const userRole = user?.role || 'user';

  const [loading, setLoading] = useState(true);
  const [rules, setRules] = useState([]);
  const [availableGeofences, setAvailableGeofences] = useState([]);
  const [selectedGeofenceId, setSelectedGeofenceId] = useState('');
  const [linking, setLinking] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [rulesRes, geofencesRes] = await Promise.all([
        geofenceApi.getEquipmentRules(equipmentId),
        geofenceApi.listGeofences(false),
      ]);
      setRules(rulesRes.data?.rules || []);
      setAvailableGeofences(geofencesRes.data?.geofences || []);
    } catch (err) {
      console.error('Erro ao carregar geofences do equipamento:', err);
    } finally {
      setLoading(false);
    }
  }, [equipmentId]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const availablePolygonGeofences = availableGeofences.filter(
    (gf) => (gf.geometry_type || (gf.point ? 'waypoint' : 'polygon')) === 'polygon',
  );
  const unlinkedGeofences = availablePolygonGeofences.filter(
    (gf) => !rules.some((r) => r.geofence_id === gf.id),
  );

  const hasOrphan = rules.some((r) =>
    (r.rules || []).some((t) => t.notify_whatsapp && !(t.notification_phones || []).length),
  );

  const handleAddGeofence = async () => {
    if (!selectedGeofenceId) return;
    setLinking(true);
    try {
      await geofenceApi.createEquipmentRule(equipmentId, {
        geofence_id: selectedGeofenceId,
        enabled: true,
        rules: [{ ...DEFAULT_GEOFENCE_TRIGGER }],
      });
      setSelectedGeofenceId('');
      await loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao vincular geofence');
    } finally {
      setLinking(false);
    }
  };

  const handleToggleRule = async (rule) => {
    try {
      await geofenceApi.updateEquipmentRule(equipmentId, rule.id, { enabled: !rule.enabled });
      setRules((prev) => prev.map((r) => (r.id === rule.id ? { ...r, enabled: !r.enabled } : r)));
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao salvar configuração de alertas');
    }
  };

  const saveTriggers = async (ruleId, nextTriggers) => {
    setRules((prev) => prev.map((r) => (r.id === ruleId ? { ...r, rules: nextTriggers } : r)));
    try {
      await geofenceApi.updateEquipmentRule(equipmentId, ruleId, {
        rules: nextTriggers.map((t) => ({ ...t, notification_phones: validPhoneList(t.notification_phones || []) })),
      });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao salvar configuração de alertas');
    }
  };

  const handleTriggerFieldChange = (ruleId, idx, updated) => {
    setRules((prev) => prev.map((r) => {
      if (r.id !== ruleId) return r;
      const next = [...(r.rules || [])];
      next[idx] = updated;
      return { ...r, rules: next };
    }));
  };

  const handleSaveTriggers = (ruleId) => {
    const rule = rules.find((r) => r.id === ruleId);
    if (rule) saveTriggers(ruleId, rule.rules || []);
  };

  const handleAddTrigger = (rule) => {
    saveTriggers(rule.id, [...(rule.rules || []), { ...DEFAULT_GEOFENCE_TRIGGER, trigger: 'on_enter' }]);
  };

  const handleDeleteTrigger = (rule, idx) => {
    const nextTriggers = (rule.rules || []).filter((_, i) => i !== idx);
    if (nextTriggers.length === 0) {
      setDeleteConfirm({ ruleId: rule.id, geofenceName: rule.geofence_name });
    } else {
      saveTriggers(rule.id, nextTriggers);
    }
  };

  const confirmDeleteRule = async () => {
    if (!deleteConfirm) return;
    try {
      await geofenceApi.deleteEquipmentRule(equipmentId, deleteConfirm.ruleId);
      setRules((prev) => prev.filter((r) => r.id !== deleteConfirm.ruleId));
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao salvar configuração de alertas');
    } finally {
      setDeleteConfirm(null);
    }
  };

  const filteredRules = rules.filter(
    (r) => matchesQuery('Geofence', query) || matchesQuery(r?.geofence_name, query),
  );

  return (
    <AlertCategorySection icon={Hexagon} label="Geofence" ruleCount={rules.length} hasOrphan={hasOrphan}>
      {loading ? (
        <div className="flex items-center gap-2 text-sm text-muted-foreground py-2">
          <Loader2 className="w-4 h-4 animate-spin" /> Carregando geofences...
        </div>
      ) : (
        <>
          <div className="flex items-center gap-2 pb-2">
            <Select value={selectedGeofenceId} onValueChange={setSelectedGeofenceId}>
              <SelectTrigger className="h-8 text-sm flex-1" data-testid="geofence-link-select">
                <SelectValue placeholder="Selecione uma geofence" />
              </SelectTrigger>
              <SelectContent>
                {unlinkedGeofences.length === 0 ? (
                  <div className="p-2 text-xs text-muted-foreground text-center">
                    Todos os polígonos já estão vinculados
                  </div>
                ) : (
                  unlinkedGeofences.map((gf) => (
                    <SelectItem key={gf.id} value={gf.id}>{gf.name}</SelectItem>
                  ))
                )}
              </SelectContent>
            </Select>
            <Button
              size="sm"
              className="h-8 text-sm"
              onClick={handleAddGeofence}
              disabled={!selectedGeofenceId || linking}
              data-testid="geofence-link-btn"
            >
              {linking && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
              Vincular
            </Button>
          </div>

          {rules.length === 0 && (
            <p className="text-xs text-muted-foreground py-2">Nenhuma regra de alerta configurada</p>
          )}

          {filteredRules.map((rule) => (
            <div
              key={rule.id}
              className={cn(
                'rounded-md border border-border/60 p-3 space-y-2 mb-2',
                rule.id === highlightId && 'ring-2 ring-primary/50 transition-all',
              )}
              data-testid={`alert-rule-row-${rule.id}`}
            >
              <div className="flex items-center justify-between gap-2">
                <span className="text-sm font-semibold truncate">{rule.geofence_name}</span>
                <div className="flex items-center gap-2">
                  <Switch
                    checked={!!rule.enabled}
                    onCheckedChange={() => handleToggleRule(rule)}
                    data-testid={`geofence-rule-toggle-${rule.id}`}
                  />
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8 text-destructive hover:bg-destructive/10"
                    onClick={() => setDeleteConfirm({ ruleId: rule.id, geofenceName: rule.geofence_name })}
                    aria-label="Remover vínculo"
                    title="Remover vínculo"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>

              <div className="space-y-2">
                {(rule.rules || []).map((trigger, idx) => (
                  <GeofenceRuleEditor
                    key={`${rule.id}-${idx}`}
                    rule={trigger}
                    onChange={(updated) => handleTriggerFieldChange(rule.id, idx, updated)}
                    onDelete={() => handleDeleteTrigger(rule, idx)}
                    compact
                    userRole={userRole}
                  />
                ))}
              </div>

              <div className="flex gap-2 pt-1">
                <Button
                  variant="outline"
                  size="sm"
                  className="h-8 text-xs flex-1"
                  onClick={() => handleAddTrigger(rule)}
                >
                  <Plus className="w-3.5 h-3.5 mr-1" /> Adicionar Gatilho
                </Button>
                <Button
                  variant="secondary"
                  size="sm"
                  className="h-8 text-xs"
                  onClick={() => handleSaveTriggers(rule.id)}
                >
                  Salvar
                </Button>
              </div>
            </div>
          ))}

          <AlertDialog open={!!deleteConfirm} onOpenChange={(open) => !open && setDeleteConfirm(null)}>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Remover vínculo?</AlertDialogTitle>
                <AlertDialogDescription>
                  Isso removerá todas as regras de alerta para a geofence "{deleteConfirm?.geofenceName}".
                  Esta ação não pode ser desfeita.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancelar</AlertDialogCancel>
                <AlertDialogAction onClick={confirmDeleteRule} className="bg-destructive hover:bg-destructive/90">
                  Remover
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </>
      )}
    </AlertCategorySection>
  );
};

const EquipmentAlertsPage = () => {
  const { equipmentId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const catParam = searchParams.get('cat') || '';
  const highlightId = searchParams.get('highlight') || '';

  const [equipment, setEquipment] = useState(null);
  const [alertConfig, setAlertConfig] = useState(null);
  const [rules, setRules] = useState([]);
  const [fuelConfig, setFuelConfig] = useState(null);
  const [liveView, setLiveView] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [query, setQuery] = useState('');

  const [reserveL, setReserveL] = useState('');
  const [savingReserve, setSavingReserve] = useState(false);

  const [ruleDialog, setRuleDialog] = useState({ open: false, rule: null, alertType: 'threshold' });
  const [deleteDialog, setDeleteDialog] = useState({ open: false, rule: null });

  const fetchAll = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [eqRes, cfgRes, rulesRes, fuelRes, liveViewRes] = await Promise.all([
        axios.get(`${API}/equipments/${equipmentId}`),
        getAlertConfig(equipmentId),
        alertsApi.getRules(equipmentId),
        axios.get(`${API}/fuel/config/${equipmentId}`),
        axios.get(`${API}/equipments/${equipmentId}/live-view`),
      ]);
      setEquipment(eqRes.data);
      setAlertConfig(cfgRes.data);
      setRules(rulesRes.data || []);
      setFuelConfig(fuelRes.data);
      setReserveL(m3ToL(fuelRes.data?.reserve_threshold_m3));
      setLiveView(liveViewRes.data);
    } catch (err) {
      console.error('Erro ao carregar alertas do equipamento:', err);
      setError('Erro ao carregar alertas do equipamento');
    } finally {
      setLoading(false);
    }
  }, [equipmentId]);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  useEffect(() => {
    if (!highlightId || loading) return undefined;
    const timer = setTimeout(() => {
      const el = document.querySelector(`[data-testid="alert-rule-row-${highlightId}"]`);
      el?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 50);
    return () => clearTimeout(timer);
  }, [highlightId, loading]);

  // Deep-link `?cat=` (AD-04, Central → "Configurar este alerta"): rola até a
  // seção correspondente. `stream_blindness` mora dentro de Conectividade;
  // `gearbox_abuse` mora dentro de Motor/Painel (mesmo cadastro por-motor).
  const CAT_SECTION_MAP = {
    engine_notification: 'engine_notification',
    gearbox_abuse: 'engine_notification',
    fuel: 'fuel',
    battery: 'battery',
    telemetry: 'telemetry',
    weather: 'weather',
    connectivity: 'connectivity',
    stream_blindness: 'connectivity',
    geofence: 'geofence',
  };

  useEffect(() => {
    const target = CAT_SECTION_MAP[catParam];
    if (!target || loading) return undefined;
    const timer = setTimeout(() => {
      const el = document.querySelector(`[data-cat-section="${target}"]`);
      el?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 50);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [catParam, loading]);

  const { fuelRules, batteryRules, rpmRules, weatherRules, offlineRules } = useMemo(() => {
    const fuel = [];
    const battery = [];
    const rpm = [];
    const weather = [];
    const offline = [];
    rules.forEach((rule) => {
      if (rule.alert_type === 'fuel_reserve') {
        fuel.push(rule);
      } else if (rule.alert_type === 'weather_route') {
        weather.push(rule);
      } else if (rule.alert_type === 'offline') {
        offline.push(rule);
      } else if (rule.alert_type === 'threshold') {
        const haystack = `${rule.name || ''} ${rule.mqtt_field || ''} ${rule.source_path || ''} ${rule.mqtt_topic || ''}`;
        if (BATTERY_HINT_RE.test(haystack)) {
          battery.push(rule);
        } else {
          rpm.push(rule);
        }
      }
    });
    return { fuelRules: fuel, batteryRules: battery, rpmRules: rpm, weatherRules: weather, offlineRules: offline };
  }, [rules]);

  // Motor/Painel (Fase 16/17.1): grupos de motor CADASTRADOS do live-view —
  // instâncias não-cadastradas ficam restritas ao editor (aba Motores),
  // mesma camada tratada de INV-LAYER-READPATH-001 (só cadastrado aparece).
  const motorGroups = useMemo(
    () => (liveView?.groups || []).filter((g) => g.kind === 'motor' && g.registered !== false),
    [liveView],
  );

  const engineOrphan = useMemo(() => {
    const hasEnabledWarning = motorGroups.some((g) =>
      Object.values(g.warnings_catalog || {}).some((w) => w?.enabled !== false),
    );
    return hasEnabledWarning && !(alertConfig?.engine_notification?.phones || []).length;
  }, [motorGroups, alertConfig]);

  const totalRules = rules.length;

  const streamBlindness = alertConfig?.stream_blindness || { phones: [], emails: [], enabled: true };
  const streamBlindnessOrphan = !!streamBlindness.enabled && !(streamBlindness.phones || []).length;

  const orphanCount = useMemo(
    () => rules.filter(ruleIsWhatsappOrphan).length + (streamBlindnessOrphan ? 1 : 0),
    [rules, streamBlindnessOrphan],
  );

  const handleNewRule = (category) => {
    const defaults = {
      fuel: 'fuel_reserve',
      weather: 'weather_route',
      connectivity: 'offline',
      telemetry: 'threshold',
      battery: 'threshold',
    };
    setRuleDialog({ open: true, rule: null, alertType: defaults[category] || 'threshold' });
  };

  const handleEditRule = (rule) => setRuleDialog({ open: true, rule, alertType: rule?.alert_type || 'threshold' });

  const handleToggleRule = async (rule) => {
    try {
      await alertsApi.updateRule(rule.id, { enabled: !rule.enabled });
      setRules((prev) => prev.map((r) => (r.id === rule.id ? { ...r, enabled: !rule.enabled } : r)));
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao salvar configuração de alertas');
    }
  };

  const handleDeleteRule = (rule) => setDeleteDialog({ open: true, rule });

  const confirmDeleteRule = async (rule) => {
    try {
      await alertsApi.deleteRule(rule.id);
      setRules((prev) => prev.filter((r) => r.id !== rule.id));
      toast.success('Regra de alerta excluída');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao salvar configuração de alertas');
    } finally {
      setDeleteDialog({ open: false, rule: null });
    }
  };

  const handleSaveReserve = async () => {
    setSavingReserve(true);
    try {
      const res = await axios.put(`${API}/fuel/config/${equipmentId}`, {
        reserve_threshold_m3: lToM3(reserveL),
      });
      setFuelConfig(res.data);
      toast.success('Limiar de reserva salvo');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao salvar configuração de alertas');
    } finally {
      setSavingReserve(false);
    }
  };

  const handleStreamBlindnessEnabled = async (enabled) => {
    setAlertConfig((prev) => ({
      ...prev,
      stream_blindness: { ...(prev?.stream_blindness || {}), enabled },
    }));
    try {
      await putAlertConfig(equipmentId, { stream_blindness: { enabled } });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao salvar configuração de alertas');
    }
  };

  // Curadoria por warning do motor (severidade/enabled) — MESMO padrão de
  // setWarning em MotorsConfig.js: o backend faz whole-replace do
  // warnings_catalog do motor, então semeamos do catálogo EFETIVO (com o
  // delta já aplicado) para nunca perder overrides de outros warnings.
  const handleWarningPatch = async (motorId, warningKey, patch) => {
    const group = (liveView?.groups || []).find((g) => g.motor_id === motorId);
    const base = group?.warnings_catalog || {};
    const nextCatalog = { ...base, [warningKey]: { ...(base[warningKey] || {}), ...patch } };
    setLiveView((prev) => ({
      ...prev,
      groups: (prev?.groups || []).map((g) => (g.motor_id === motorId ? { ...g, warnings_catalog: nextCatalog } : g)),
    }));
    try {
      await axios.put(`${API}/equipments/${equipmentId}/live-config`, {
        motors: [{ id: motorId, warnings_catalog: nextCatalog }],
      });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao salvar configuração de alertas');
    }
  };

  const equipmentName = equipment?.name || '';

  return (
    <div className="page-container space-y-6">
      <div className="flex items-center gap-3">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => navigate('/equipments')}
          data-testid="back-btn"
        >
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <h1 className="text-2xl font-semibold">
          Alertas — {equipmentName || '...'}
        </h1>
      </div>

      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-3 text-sm text-muted-foreground">
          <span data-testid="rules-configured-count">{totalRules} regras configuradas</span>
          {orphanCount > 0 && (
            <Badge
              variant="outline"
              className={cn(
                'text-xs',
                severityConfig.critical.bg,
                severityConfig.critical.color,
                severityConfig.critical.border,
              )}
              data-testid="orphan-count-badge"
            >
              {orphanCount} sem destinatário
            </Badge>
          )}
        </div>
        <div className="relative w-full sm:w-80">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            placeholder="Buscar por categoria, motor ou regra..."
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            className="pl-9"
            data-testid="alerts-search-input"
          />
        </div>
      </div>

      {loading && (
        <div className="flex items-center justify-center py-16 text-muted-foreground">
          <Loader2 className="w-6 h-6 animate-spin mr-2" />
          Carregando alertas...
        </div>
      )}

      {!loading && error && (
        <Card className="border-destructive/50">
          <CardContent className="flex flex-col items-center gap-3 py-10">
            <AlertTriangle className="w-8 h-8 text-destructive" />
            <p className="text-sm text-muted-foreground">Erro ao carregar alertas do equipamento</p>
            <Button variant="outline" onClick={fetchAll}>Tentar novamente</Button>
          </CardContent>
        </Card>
      )}

      {!loading && !error && (
        <div className="space-y-4">
          <div data-cat-section="engine_notification" className="space-y-4">
            <AlertCategorySection
              icon={Cog}
              label="Motor/Painel"
              ruleCount={motorGroups.length}
              hasOrphan={engineOrphan}
            >
              <div className="pb-2 border-b border-border/40 mb-2">
                <p className="text-xs font-semibold text-muted-foreground uppercase mb-2">
                  Destinatários da categoria
                </p>
                <AlertRecipientsEditor
                  equipmentId={equipmentId}
                  category="engine_notification"
                  section={alertConfig?.engine_notification}
                  notifyWhatsapp
                  notifyEmail
                  onSaved={fetchAll}
                />
              </div>

              {motorGroups.length === 0 && (
                <p className="text-xs text-muted-foreground py-2">
                  Nenhum motor cadastrado ainda — cadastre motores na aba Motores do equipamento.
                </p>
              )}

              {motorGroups
                .filter((g) => matchesQuery('Motor/Painel', query) || matchesQuery(g?.label, query))
                .map((group) => {
                  const warnings = Object.entries(group.warnings_catalog || {}).sort(([a], [b]) => a.localeCompare(b));
                  const engineRules = Object.entries(group.alert_rules || {});
                  const gearbox = group.gearbox || {};
                  const gearboxCalibrated = Number(gearbox.ratio_rpm_s) > 0;
                  return (
                    <div
                      key={group.key || group.motor_id}
                      className="rounded-md border border-border/60 p-3 space-y-2 mb-2"
                      data-testid={`engine-motor-${group.motor_id}`}
                    >
                      <div className="flex items-center justify-between gap-2">
                        <span className="text-sm font-semibold truncate">{group.label}</span>
                        <Button
                          variant="link"
                          size="sm"
                          className="h-auto p-0 text-xs"
                          onClick={() => navigate(`/equipments?edit=${equipmentId}&tab=motores`)}
                        >
                          Editar na aba Motores
                        </Button>
                      </div>

                      {warnings.length === 0 && (
                        <p className="text-xs text-muted-foreground">Catálogo de avisos vazio para este motor.</p>
                      )}
                      {warnings
                        .filter(([key, cfg]) => matchesQuery(cfg?.label || key, query) || matchesQuery(group?.label, query) || matchesQuery('Motor/Painel', query))
                        .map(([warningKey, cfg]) => {
                          const warningRuleId = `engine_notification:${group.motor_id}:${warningKey}`;
                          return (
                            <div
                              key={warningKey}
                              className={cn(
                                'flex items-center gap-3 py-1.5 border-b border-border/40 last:border-b-0',
                                cfg?.enabled === false && 'opacity-50',
                                warningRuleId === highlightId && 'ring-2 ring-primary/50 rounded-md transition-all',
                              )}
                              data-testid={`alert-rule-row-${warningRuleId}`}
                            >
                              <div className="flex-1 min-w-0">
                                <span className="text-sm truncate">{cfg?.label || warningKey}</span>
                                <p className="text-xs text-muted-foreground">herda destinatários da categoria</p>
                              </div>
                              <Select
                                value={cfg?.severity || 'medium'}
                                onValueChange={(value) => handleWarningPatch(group.motor_id, warningKey, { severity: value })}
                              >
                                <SelectTrigger className="h-8 w-[110px] text-xs" data-testid={`engine-warning-severity-${group.motor_id}-${warningKey}`}>
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  {SEVERITY_OPTIONS.map((opt) => (
                                    <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                              <Switch
                                checked={cfg?.enabled !== false}
                                onCheckedChange={(checked) => handleWarningPatch(group.motor_id, warningKey, { enabled: checked })}
                                data-testid={`engine-warning-enabled-${group.motor_id}-${warningKey}`}
                              />
                            </div>
                          );
                        })}

                      {engineRules.length > 0 && (
                        <div className="pt-2 border-t border-border/40 space-y-1">
                          <p className="text-[11px] uppercase text-muted-foreground">Regras por motor</p>
                          {engineRules.map(([ruleId, rule]) => {
                            const sev = severityConfig[rule?.severity] || severityConfig.medium;
                            return (
                              <div key={ruleId} className="flex items-center gap-2 text-xs text-muted-foreground">
                                <Badge variant="outline" className={cn('text-xs', sev.color, sev.border)}>
                                  {sev.label}
                                </Badge>
                                <span className="truncate flex-1">{rule?.label || ruleId}</span>
                                <span>{rule?.enabled !== false ? 'Ativo' : 'Inativo'}</span>
                              </div>
                            );
                          })}
                        </div>
                      )}

                      <div className="pt-2 border-t border-border/40 flex items-center gap-2 text-xs text-muted-foreground">
                        <ArrowLeftRight className="w-3.5 h-3.5 shrink-0" />
                        {gearboxCalibrated ? (
                          <span>
                            Reversão da gearbox calibrada (ratio {gearbox.ratio_rpm_s} RPM/s, engate seguro {gearbox.safe_engage_rpm ?? '—'} RPM)
                          </span>
                        ) : (
                          <span>Reversão da gearbox em modo sombra (sem ratio calibrado) — configure na aba Motores.</span>
                        )}
                      </div>
                    </div>
                  );
                })}
            </AlertCategorySection>
          </div>

          <div data-cat-section="fuel" className="space-y-4">
            <RuleCategorySection
              category="fuel"
              label="Combustível"
              icon={Fuel}
              rules={fuelRules}
              query={query}
              highlightId={highlightId}
              onNewRule={handleNewRule}
              onEditRule={handleEditRule}
              onToggleRule={handleToggleRule}
              onDeleteRule={handleDeleteRule}
            />

            {/* Limiar de reserva (999.4, GET/PUT /api/fuel/config/{id}) embutido logo
                abaixo da regra fuel_reserve — mesma seção Combustível. */}
            <div className="rounded-md border border-border bg-muted/20 p-4">
              <Label className="text-xs text-muted-foreground">Limiar de reserva de combustível (Litros)</Label>
              <div className="flex items-center gap-2 mt-1">
                <Input
                  type="number"
                  min="0"
                  value={reserveL}
                  onChange={(e) => setReserveL(e.target.value)}
                  className="h-8 w-40 text-sm"
                  data-testid="fuel-reserve-threshold-input"
                />
                <Button size="sm" className="h-8 text-sm" onClick={handleSaveReserve} disabled={savingReserve}>
                  {savingReserve && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
                  Salvar
                </Button>
              </div>
            </div>
          </div>

          <div data-cat-section="battery">
            <RuleCategorySection
              category="battery"
              label="Bateria"
              icon={BatteryWarning}
              rules={batteryRules}
              query={query}
              highlightId={highlightId}
              onNewRule={handleNewRule}
              onEditRule={handleEditRule}
              onToggleRule={handleToggleRule}
              onDeleteRule={handleDeleteRule}
            />
          </div>

          <div data-cat-section="telemetry">
            <RuleCategorySection
              category="telemetry"
              label="Telemetria/RPM"
              icon={Gauge}
              rules={rpmRules}
              query={query}
              highlightId={highlightId}
              onNewRule={handleNewRule}
              onEditRule={handleEditRule}
              onToggleRule={handleToggleRule}
              onDeleteRule={handleDeleteRule}
            />
          </div>

          <div data-cat-section="connectivity">
            <AlertCategorySection
              icon={Unplug}
              label="Conectividade"
              ruleCount={offlineRules.length}
              hasOrphan={offlineRules.some(ruleIsWhatsappOrphan) || streamBlindnessOrphan}
            >
              <div className="space-y-2">
                <p className="text-xs font-semibold text-muted-foreground uppercase">Offline</p>
                {offlineRules
                  .filter((r) => matchesQuery('Conectividade offline', query) || matchesQuery(r?.name, query))
                  .map((rule) => (
                    <div
                      key={rule.id}
                      className={cn(rule.id === highlightId && 'ring-2 ring-primary/50 rounded-md transition-all')}
                      data-testid={`alert-rule-row-${rule.id}`}
                    >
                      <AlertRuleRow
                        rule={rule}
                        recipientsSummary={ruleRecipientsSummary(rule)}
                        onToggleEnabled={handleToggleRule}
                        onEdit={handleEditRule}
                        onDelete={handleDeleteRule}
                      />
                    </div>
                  ))}
                <Button size="sm" variant="outline" className="h-8 text-sm" onClick={() => handleNewRule('connectivity')}>
                  Nova regra
                </Button>
              </div>

              <div className="space-y-2 pt-4 border-t border-border">
                <p className="text-xs font-semibold text-muted-foreground uppercase">Cegueira de stream (SignalK)</p>
                <div className="flex items-center justify-between">
                  <Label className="text-sm">Alertar quando a leitura de bordo parar de chegar</Label>
                  <Switch
                    checked={!!streamBlindness.enabled}
                    onCheckedChange={handleStreamBlindnessEnabled}
                    data-testid="stream-blindness-enabled-switch"
                  />
                </div>
                <AlertRecipientsEditor
                  equipmentId={equipmentId}
                  category="stream_blindness"
                  section={streamBlindness}
                  notifyWhatsapp={!!streamBlindness.enabled}
                  notifyEmail={!!streamBlindness.enabled}
                  onSaved={fetchAll}
                />
              </div>
            </AlertCategorySection>
          </div>

          <div data-cat-section="geofence">
            <GeofenceCategorySection equipmentId={equipmentId} query={query} highlightId={highlightId} />
          </div>

          <div data-cat-section="weather">
            <RuleCategorySection
              category="weather"
              label="Clima"
              icon={CloudSun}
              rules={weatherRules}
              query={query}
              highlightId={highlightId}
              onNewRule={handleNewRule}
              onEditRule={handleEditRule}
              onToggleRule={handleToggleRule}
              onDeleteRule={handleDeleteRule}
            />
          </div>
        </div>
      )}

      <AlertRuleDialog
        open={ruleDialog.open}
        onOpenChange={(open) => setRuleDialog((prev) => ({ ...prev, open }))}
        rule={ruleDialog.rule}
        equipmentId={equipmentId}
        onSaved={fetchAll}
      />
      <DeleteRuleDialog
        open={deleteDialog.open}
        onOpenChange={(open) => setDeleteDialog((prev) => ({ ...prev, open }))}
        rule={deleteDialog.rule}
        onConfirm={confirmDeleteRule}
      />
    </div>
  );
};

export default EquipmentAlertsPage;
